import { Config, makeRequest } from "@paywithglide/glide-js";
import { logger } from "../logger";

export type CreateOnrampSessionRequest = {
  sessionId: string; // widget session ID
  email?: string; // optional
  kycRedirectUrl?: string; // optional, where to redirect after KYC
};

export type CreateOnrampSessionResponse = {
  customerId: string;
  kycUrl: string;
  kycStatus: string | null;
};

/**
 * Creates an onramp session using the phone number from the widget session.
 * The phone number is securely retrieved from the backend (not passed from client).
 * This ensures the phone number was verified by the client's API call.
 */
export async function createOnrampSession(
  glideConfig: Config,
  request: CreateOnrampSessionRequest,
): Promise<CreateOnrampSessionResponse> {
  try {
    const response = await makeRequest<CreateOnrampSessionResponse>(
      glideConfig,
      {
        url: `/onramp/session`,
        method: "POST",
        body: request,
      },
    );

    return response;
  } catch (error) {
    logger.error(`Failed to create Onramp session: ${error}`);
    throw error;
  }
}

// KYC Status Categories (all lowercase for case-insensitive comparison)
const KYC_PASSED_STATUSES = [
  "completed",
  "basic_kyc_completed",
  "intermediate_kyc_completed",
  "advance_kyc_completed",
  "edd_completed",
];

const KYC_NEEDS_LOGIN_STATUSES = ["login_required", "otp_completed"];

const KYC_IN_REVIEW_STATUSES = ["in_review"];

const KYC_TEMPORARY_FAILURE_STATUSES = ["temporary_failure"];

const KYC_PERMANENT_FAILURE_STATUSES = ["permanent_failure"];

export type KycStatusCategory =
  | "passed"
  | "needs_login"
  | "in_review"
  | "temporary_failure"
  | "permanent_failure"
  | "unknown";

/**
 * Determines the category of a KYC status (case-insensitive).
 */
export function getKycStatusCategory(
  kycStatus: string | null,
): KycStatusCategory {
  if (!kycStatus) return "needs_login";

  const status = kycStatus.toLowerCase();

  if (KYC_PASSED_STATUSES.includes(status)) return "passed";
  if (KYC_NEEDS_LOGIN_STATUSES.includes(status)) return "needs_login";
  if (KYC_IN_REVIEW_STATUSES.includes(status)) return "in_review";
  if (KYC_TEMPORARY_FAILURE_STATUSES.includes(status))
    return "temporary_failure";
  if (KYC_PERMANENT_FAILURE_STATUSES.includes(status))
    return "permanent_failure";

  return "unknown";
}

/**
 * Checks if the KYC status indicates the user has completed verification.
 */
export function isKycCompleted(kycStatus: string | null): boolean {
  return getKycStatusCategory(kycStatus) === "passed";
}
